import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import CheckoutActionButton from "../ui/CheckoutActionButton.tsx";
import CheckoutFieldRow from "../ui/CheckoutFieldRow.tsx";
import DiscountInputRow from "../ui/DiscountInputRow.tsx";
import { getGiftCardInlineError } from "../utils/toastHelpers.ts";
import { ORDER_TYPE } from "../utils/constants.ts";
import type { RootState, UseAllTogetherDiscountsReturn } from "../hooks/types.ts";

interface DiscountPanelProps {
  discounts: UseAllTogetherDiscountsReturn;
  tableDetail?: {
    activeOrder?: {
      _id?: string;
      service_charge_percentage?: number;
      has_split_payment?: boolean;
    };
  };
}

const DiscountPanel: React.FC<DiscountPanelProps> = ({
  discounts,
  tableDetail,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const orderType = (location.state?.type ? location.state?.type : searchParams.get("type")) ?? "";
  const restSettingDetails = useSelector((state: RootState) => state?.restSettings?.data);

  const {
    formik,
    serviceChargeFormik,
    couponCodeApplied,
    giftCardApplied,
    handleCouponCodeChange: onCouponToggle,
    handleGiftCardChange: onGiftCardToggle,
    applyCouponCodeAmount: onApplyCoupon,
    applyGiftCardAmount: onApplyGiftCard,
    handleServiceChargePercentageChange: onServiceChargeToggle,
    applyServiceCharge: onApplyServiceCharge,
  } = discounts;

  if (!orderType || orderType === ORDER_TYPE.EDIT_SALES) {
    return null;
  }

  const hasSplitPayment = !!tableDetail?.activeOrder?.has_split_payment;
  const isServiceChargeApplied =
    serviceChargeFormik.values.is_service_charge_percentage &&
    Number(tableDetail?.activeOrder?.service_charge_percentage || 0) > 0;
  const giftCardInlineError = getGiftCardInlineError(t, formik.values.gift_card_amount);

  return (
    <div className="checkout-discount-shell">
      <div className="split-discount-collapse-body">
        <DiscountInputRow
          label={t("label_coupon_code")}
          placeholder={t("label_enter_coupon")}
          value={formik.values.couponCode}
          isApplied={couponCodeApplied}
          onApply={onApplyCoupon}
          onRemove={() => onCouponToggle(false)}
          onChange={(val) => {
            onCouponToggle(true);
            formik.setFieldValue("couponCode", val);
          }}
          disabled={hasSplitPayment}
          isApplyDisabled={couponCodeApplied || !formik.values.couponCode?.trim()}
          error={formik.touched.couponCode && formik.errors.couponCode ? <>{formik.errors.couponCode}</> : undefined}
          applyLabel={t("label_apply")}
          appliedLabel={t("label_applied")}
          removeLabel={t("remove")}
        />

        <DiscountInputRow
          label={t("label_gift_card")}
          placeholder={t("enter_gift_card_amount")}
          value={formik.values.gift_card_amount}
          isApplied={giftCardApplied}
          onApply={onApplyGiftCard}
          onRemove={() => onGiftCardToggle(false)}
          onChange={(val) => {
            onGiftCardToggle(true);
            formik.setFieldValue("gift_card_amount", val);
            formik.setFieldTouched?.("gift_card_amount", true, true);
          }}
          isMoneyInput
          disabled={hasSplitPayment}
          isApplyDisabled={giftCardApplied || !formik.values.gift_card_amount || !!giftCardInlineError}
          error={giftCardInlineError ? <>{giftCardInlineError}</> : undefined}
          applyLabel={t("label_apply")}
          appliedLabel={t("label_applied")}
          removeLabel={t("remove")}
        />

        {restSettingDetails?.service_charge_applicable_condition !== "always" &&
        restSettingDetails?.is_service_charge_enabled ? (
          <CheckoutFieldRow
            label={`${t("service_charges")} (%)`}
            actionLabel={isServiceChargeApplied ? t("remove") : undefined}
            onAction={() => onServiceChargeToggle(false)}
            error={
              serviceChargeFormik.touched.service_charge_percentage &&
              serviceChargeFormik.errors.service_charge_percentage ? (
                <>{serviceChargeFormik.errors.service_charge_percentage}</>
              ) : undefined
            }
          >
            <div className="split-discount-control-row">
              <input
                type="number"
                className="form-control no_spring_input"
                placeholder={t("enter_service_charge")}
                value={serviceChargeFormik.values.service_charge_percentage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onServiceChargeToggle(true);
                  serviceChargeFormik.setFieldValue("service_charge_percentage", e.target.value);
                }}
              />

              <CheckoutActionButton
                disabled={
                  Object.keys(serviceChargeFormik.errors).length > 0 ||
                  serviceChargeFormik.values.service_charge_percentage ===
                    tableDetail?.activeOrder?.service_charge_percentage
                }
                onClick={onApplyServiceCharge}
              >
                {t("label_apply")}
              </CheckoutActionButton>
            </div>
          </CheckoutFieldRow>
        ) : null}
      </div>
    </div>
  );
};

export default DiscountPanel;
